import React from "react";
import { useNavigate } from "react-router-dom";

export default function ArtAdvisorDemo() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "👥",
      title: "Gestion multi-clients",
      description: "Gérez jusqu'à 5 collections clients depuis une interface unique. Basculez facilement d'un portefeuille à l'autre.",
      demo: "Interface de sélection rapide avec vue d'ensemble des 5 collections actives"
    },
    {
      icon: "📊",
      title: "Rapports personnalisés",
      description: "Générez des rapports de collection avec votre identité visuelle pour vos clients.",
      demo: "Aperçu PDF avec logo personnalisé et mise en page professionnelle"
    },
    {
      icon: "🎨",
      title: "Outils de présentation",
      description: "Créez des présentations visuelles pour vos recommandations d'acquisition.",
      demo: "Portfolio visuel avec comparatifs d'œuvres et analyses de marché"
    },
    {
      icon: "⚡",
      title: "Support prioritaire",
      description: "Accès à notre équipe de support avec temps de réponse garanti sous 4h.",
      demo: "Chat dédié et ligne directe avec l'équipe technique"
    }
  ];

  const screenshots = [
    {
      title: "Dashboard multi-clients",
      description: "Vue d'ensemble de tous vos portefeuilles clients avec KPIs en temps réel",
      image: "🖼️"
    },
    {
      title: "Rapports co-marqués",
      description: "Documents PDF professionnels avec votre logo et votre charte graphique",
      image: "📄"
    },
    {
      title: "Gestion des invitations",
      description: "Invitez vos clients à consulter leur collection en toute sécurité",
      image: "✉️"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-neutral-50 to-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-black text-white text-sm font-semibold rounded-full mb-6">
              PLAN ART ADVISOR
            </div>
            <h1 
              className="text-5xl md:text-6xl font-serif mb-6 tracking-tight"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Gérez plusieurs collections clients comme un pro
            </h1>
            <p className="text-xl text-neutral-500 mb-8 leading-relaxed">
              Les outils essentiels pour les art advisors qui accompagnent
              jusqu'à 5 collectionneurs simultanément
            </p>
            <div className="flex items-baseline justify-center gap-2 mb-8">
              <span className="text-5xl font-bold">99 €</span>
              <span className="text-2xl text-neutral-500">/mois</span>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate("/register?plan=art_advisor")}
                className="px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-neutral-800 transition-all duration-300 text-lg"
              >
                Démarrer maintenant
              </button>
              <button
                onClick={() => navigate("/contact?plan=art_advisor&demo=true")}
                className="px-8 py-4 border-2 border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-all duration-300 text-lg"
              >
                Demander une démo live
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features détaillées */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl font-serif mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Tout ce dont vous avez besoin
          </h2>
          <p className="text-xl text-neutral-500">
            Des fonctionnalités pensées pour votre métier d'art advisor
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {features.map((feature, idx) => (
            <div key={idx} className="group">
              <div className="mb-4 text-5xl">{feature.icon}</div>
              <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                {feature.description}
              </p>
              <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                <p className="text-sm text-neutral-500 italic">{feature.demo}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Screenshots Section */}
      <div className="bg-neutral-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-serif mb-4"
              style={{ fontFamily: "Georgia, serif" }}
            >
              L'interface en images
            </h2>
            <p className="text-xl text-neutral-500">
              Découvrez comment Artkiv simplifie votre quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {screenshots.map((shot, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-neutral-200 hover:shadow-lg transition-shadow">
                <div className="text-6xl mb-4 text-center">{shot.image}</div>
                <h4 className="text-lg font-semibold mb-2">{shot.title}</h4>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {shot.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comparaison avec Premium */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-neutral-50 to-white rounded-2xl p-10 border border-neutral-200">
          <h3 className="text-2xl font-semibold mb-6 text-center">
            Besoin de plus ? Passez en Art Advisor Premium
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-lg">Art Advisor (99€)</h4>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li>✓ Jusqu'à 5 clients</li>
                <li>✓ Rapports standards</li>
                <li>✓ Support prioritaire</li>
                <li>✗ IA limitée</li>
                <li>✗ Pas d'API</li>
              </ul>
            </div>
            <div className="border-l-2 border-black pl-6">
              <h4 className="font-semibold mb-3 text-lg">Art Advisor Premium (199€)</h4>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li>✓ Clients illimités</li>
                <li>✓ IA complète pour tous</li>
                <li>✓ Co-marquage avancé</li>
                <li>✓ Tableau de bord agrégé</li>
                <li>✓ API & intégrations (en développement)</li>
              </ul>
            </div>
          </div>
          <div className="text-center mt-8">
            <button
              onClick={() => navigate("/plans/art-advisor-premium/demo")}
              className="px-6 py-3 bg-black text-white rounded-full hover:bg-neutral-800 transition-colors"
            >
              Découvrir Art Advisor Premium
            </button>
          </div>
        </div>
      </div>

      {/* Témoignages */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-serif mb-4" style={{ fontFamily: "Georgia, serif" }}>
              Ils nous font confiance
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-neutral-900 p-8 rounded-2xl">
              <p className="text-lg mb-4 italic leading-relaxed">
                "Artkiv m'a permis de professionnaliser mes services. Mes clients
                adorent recevoir leurs rapports personnalisés."
              </p>
              <p className="text-sm text-neutral-400">
                — Sophie M., Art Advisor, Paris
              </p>
            </div>
            <div className="bg-neutral-900 p-8 rounded-2xl">
              <p className="text-lg mb-4 italic leading-relaxed">
                "La gestion multi-clients est un gain de temps énorme. Je peux
                suivre 5 collections sans effort."
              </p>
              <p className="text-sm text-neutral-400">
                — Marc L., Conseiller en patrimoine artistique, Lyon
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Final */}
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 
          className="text-4xl font-serif mb-6"
          style={{ fontFamily: "Georgia, serif" }}
        >
          Prêt à transformer votre activité ?
        </h2>
        <p className="text-xl text-neutral-500 mb-8 max-w-2xl mx-auto">
          Rejoignez les art advisors qui gèrent leurs collections clients avec Artkiv
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => navigate("/register?plan=art_advisor")}
            className="px-10 py-4 bg-black text-white rounded-full font-medium hover:bg-neutral-800 transition-all duration-300 text-lg"
          >
            Commencer gratuitement
          </button>
          <button
            onClick={() => navigate("/contact?plan=art_advisor")}
            className="px-10 py-4 border-2 border-neutral-300 text-neutral-700 rounded-full font-medium hover:border-black hover:text-black transition-all duration-300 text-lg"
          >
            Parler à l'équipe
          </button>
        </div>
        <p className="text-sm text-neutral-500 mt-6">
          Sans engagement • Annulable à tout moment
        </p>
      </div>
    </div>
  );
}